import {
    createCurrencyFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import CheckIcons from '@/icons/CheckIcons'
import { generatePathDatacentral, paths } from '@/utils/path'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { RevenueCombined } from '../../types'

export const getTableColumns = (
    t: TFunction<'translation'>
): DataTableColumn<RevenueCombined>[] => [
    {
        key: 'revenue_month',
        title: t('revenue_month'),
        width: 100,
    },
    {
        key: 'customer_name',
        title: t('customer_name'),
        width: 250,
        cellClassName: 'truncate',
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({
                id: row.customer_id.toString(),
            }),
            label: (_, row) => `${row.customer_id} - ${row.customer_name}`,
        }),
    },
    {
        key: 'product_group_name',
        title: t('product_group_name'),
        width: 320,
        render: (_, row) => {
            if (!row.product_group_name)
                return <span className="text-xs text-gray-500">-</span>
            return (
                <span
                    className="text-xs text-gray-700 truncate block"
                    title={row.product_group_name}
                >
                    {row.product_group_name}
                </span>
            )
        },
    },
    {
        key: 'invoice_id',
        title: t('invoice_id'),
        width: 100,
        render: (value, row) => {
            if (!row.invoice_id)
                return <span className="text-xs text-gray-500">-</span>
            return (
                <Link
                    to={generatePathDatacentral(paths.invoiceDetail, {
                        id: row.invoice_id,
                    })}
                    className="text-xs text-primary hover:underline"
                >
                    #{value}
                </Link>
            )
        },
    },
    {
        key: 'is_allocated',
        title: t('allocations.allocated'),
        width: 150,
        cellClassName: 'truncate',
        render: (value) => {
            if (!value)
                return (
                    <span className="text-xs text-gray-400 italic">
                        {t('not_allocated')}
                    </span>
                )
            return (
                <span className="text-xs text-gray-700">{t('allocated')}</span>
            )
        },
    },
    {
        key: 'revenue_status',
        title: t('revenue_type_status'),
        width: 120,
        cellClassName: 'truncate',
        render: (_, row) => {
            const isProjected = !row.invoice_id || row.is_projected
            return (
                <span
                    className={`text-xs ${isProjected ? 'text-amber-600' : 'text-gray-700'}`}
                >
                    {isProjected ? t('projected') : t('actual')}
                </span>
            )
        },
    },
    {
        key: 'revenue_type',
        title: t('revenue_type'),
        width: 130,
        formatter: createOptionsFormatter({
            options: [
                {
                    label: t('payment_type.renewal'),
                    value: 'renewal',
                    color: 'primary',
                },
                {
                    label: t('payment_type.new_sale'),
                    value: 'new_sale',
                    color: 'success',
                },
                {
                    label: t('payment_type.upsell'),
                    value: 'upsell',
                    color: 'warning',
                },
            ],
        }),
    },
    {
        key: 'allocated_amount',
        title: t('revenue_amount'),
        width: 130,
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ hideZero: true, currency: '' }),
    },
    {
        key: 'is_paid',
        title: t('allocations.payment_status'),
        width: 80,
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        render: (value, row) => {
            if (row.is_projected || !row.invoice_id)
                return <span className="text-gray-400">-</span>
            return value ? (
                <CheckIcons className="w-4 h-4 text-success inline-block" />
            ) : (
                <span className="text-gray-400">-</span>
            )
        },
    },
    {
        key: 'payment_date',
        title: t('payment_date'),
        width: 80,
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        render: (value, row) => {
            if (!row.is_paid || !value)
                return <span className="text-xs text-gray-400">-</span>
            const date = new Date(value)
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return (
                <span className="text-xs text-gray-600">
                    {month}/{year}
                </span>
            )
        },
    },
]
